'use client';

import { type InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import dynamic from 'next/dynamic';
import React from 'react';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import ExperimentsLoaded from '@/vdp/ExperimentSetter';
import VehicleDetailsData from '@/vdp/VehicleDetailsContainer/VehicleDetailsData';
import { vdpStandardPage } from '~/constants/eventable';
import { CampaignTemplate } from '~/constants/vdp/campaignTemplate';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/vdp/experiments';
import {
  AcquisitionContextProvider,
  type OwnProps as AcquisitionContextProps
} from '~/context/common/AcquisitionContext';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import { FactoryUpgradesQualiProvider } from '~/context/common/FactoryUpgradesQualiContext';
import {
  NativeContextProvider,
  type OwnProps as NativeContextProps
} from '~/context/common/NativeContext';
import { SebastianContextProvider } from '~/context/common/SebastianContext';
import { ShareContextProvider } from '~/context/common/ShareContext';
import {
  StaticContextProvider as CommonStaticContextProvider,
  type OwnProps as CommonStaticContextProps
} from '~/context/common/StaticContext';
import TradeContext, {
  TradeContextProvider,
  type OwnProps as TradeContextProps
} from '~/context/common/TradeContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/context/common/UserContext';
import { DiscountContextProvider } from '~/context/vdp/DiscountContext';
import { CarouselContextProvider } from './CarouselContext';
import { DeliveryPickupContextProvider } from './DeliveryPickupContext';
import { FinanceContextProvider } from './FinanceContext';
import FinanceData from './FinanceContext/FinanceData';
import {
  GlobalContextProvider,
  type OwnProps as GlobalContextProps
} from './GlobalContext';
import { ModalContextProvider, type ModalContextProps } from './ModalContext';
import { SectionReadinessProvider } from './SectionReadinessContext';
import {
  SpinnerContextProvider,
  type SpinnerContextProps
} from './SpinnerContext';
import { StarRatingOwnerReviewContextProvider } from './StarRatingOwnerReviewContext';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from './StaticContext';
import { StickyBannerContextProvider } from './StickyBannerContext';
import { UiContextProvider } from './UiContext';
import {
  UnlockTimerContextProvider,
  type OwnProps as UnlockTimerContextProps
} from './UnlockTimerContext';
import {
  UserVehicleContextProvider,
  type OwnProps as UserVehicleContextProps
} from './UserVehicleContext';
import {
  VehicleContextProvider,
  type OwnProps as VehicleContextProps
} from './VehicleContext';
import { VehiclesAvailableSoonerContextProvider } from './VehiclesAvailableSoonerContext';
import { DeferredActionProvider } from '../common/DeferredActionContext';
import { ErrorContextProvider } from '../common/ErrorContext';

const CoreAuth = dynamic(
  () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
  { ssr: false }
);

export type VehicleDetailsProvidersProps = {
  forAcquisitionContext: AcquisitionContextProps;
  forAnalyticsContext: {
    correlationId: string;
    payloadMetaAttributes: {
      campaignTemplate: CampaignTemplate | null;
      dealershipId?: string;
      inventoryType: InventoryTypeKey;
      isWebview: boolean;
      offering: VehicleOffering | null;
      page: string;
      searchRequestId?: string;
      vehicleId: number;
      webview: {
        platform: 'ios' | 'android' | null;
        version: string;
      } | null;
    };
    track: string;
  };
  forCommonStaticContext: CommonStaticContextProps;
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forGlobalContext: GlobalContextProps;
  forModalContext: ModalContextProps;
  forNativeContext: NativeContextProps;
  forSpinnerContext: SpinnerContextProps;
  forStaticContext: StaticContextProps;
  forTradeContext: TradeContextProps;
  forUnlockTimerContext: UnlockTimerContextProps;
  forUserContext: UserContextProps;
  forUserVehicleContext: UserVehicleContextProps;
  forVehicleContext: VehicleContextProps;
};

type OwnProps = React.PropsWithChildren<VehicleDetailsProvidersProps>;

export default function VehicleDetailsProviders({
  children,
  forAcquisitionContext,
  forAnalyticsContext,
  forCommonStaticContext,
  forDeviceContext,
  forExperimentContext,
  forGlobalContext,
  forModalContext,
  forNativeContext,
  forSpinnerContext,
  forStaticContext,
  forTradeContext,
  forUnlockTimerContext,
  forUserContext,
  forUserVehicleContext,
  forVehicleContext
}: OwnProps) {
  return (
    <CommonStaticContextProvider {...forCommonStaticContext}>
      <StaticContextProvider {...forStaticContext}>
        <NativeContextProvider {...forNativeContext}>
          <ExperimentProvider
            {...forExperimentContext}
            browserCookieIdExperiments={browserCookieIdExperiments}
            userIdExperiments={userIdExperiments}
          >
            <AnalyticsProvider
              projectName={vdpStandardPage}
              {...forAnalyticsContext}
            >
              <DeviceContextProvider {...forDeviceContext}>
                <DeferredActionProvider>
                  <ErrorContextProvider>
                    <VehicleContextProvider {...forVehicleContext}>
                      <UserContextProvider {...forUserContext}>
                        <GlobalContextProvider {...forGlobalContext}>
                          <AcquisitionContextProvider
                            {...forAcquisitionContext}
                          >
                            <ExperimentsLoaded />
                            <UiContextProvider>
                              <FinanceContextProvider>
                                <TradeContextProvider {...forTradeContext}>
                                  <TradeContext.Consumer>
                                    {({ activeTrade, isTradeInApplied }) => (
                                      <DiscountContextProvider
                                        hasActiveTrade={Boolean(activeTrade)}
                                        isTradeInApplied={isTradeInApplied}
                                      >
                                        <FinanceData />
                                        <PortalContextProvider>
                                          <ShareContextProvider>
                                            <SpinnerContextProvider
                                              {...forSpinnerContext}
                                            >
                                              <StarRatingOwnerReviewContextProvider>
                                                <ModalContextProvider
                                                  {...forModalContext}
                                                >
                                                  <UserVehicleContextProvider
                                                    {...forUserVehicleContext}
                                                  >
                                                    <UnlockTimerContextProvider
                                                      {...forUnlockTimerContext}
                                                    >
                                                      <DeliveryPickupContextProvider>
                                                        <VehiclesAvailableSoonerContextProvider>
                                                          <CarouselContextProvider>
                                                            <StickyBannerContextProvider>
                                                              <SebastianContextProvider>
                                                                <FactoryUpgradesQualiProvider>
                                                                  <SectionReadinessProvider>
                                                                    <CoreAuth />
                                                                    <VehicleDetailsData />
                                                                    {children}
                                                                  </SectionReadinessProvider>
                                                                </FactoryUpgradesQualiProvider>
                                                              </SebastianContextProvider>
                                                            </StickyBannerContextProvider>
                                                          </CarouselContextProvider>
                                                        </VehiclesAvailableSoonerContextProvider>
                                                      </DeliveryPickupContextProvider>
                                                    </UnlockTimerContextProvider>
                                                  </UserVehicleContextProvider>
                                                </ModalContextProvider>
                                              </StarRatingOwnerReviewContextProvider>
                                            </SpinnerContextProvider>
                                          </ShareContextProvider>
                                        </PortalContextProvider>
                                      </DiscountContextProvider>
                                    )}
                                  </TradeContext.Consumer>
                                </TradeContextProvider>
                              </FinanceContextProvider>
                            </UiContextProvider>
                          </AcquisitionContextProvider>
                        </GlobalContextProvider>
                      </UserContextProvider>
                    </VehicleContextProvider>
                  </ErrorContextProvider>
                </DeferredActionProvider>
              </DeviceContextProvider>
            </AnalyticsProvider>
          </ExperimentProvider>
        </NativeContextProvider>
      </StaticContextProvider>
    </CommonStaticContextProvider>
  );
}
